import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { baseEn } from './base-en';
import { baseZh } from './base-zh';

export type Language = 'en' | 'zh';

// Cookie helpers for cross-domain sync
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]*)'));
  return match ? match[2] : null;
}

function setCookie(name: string, value: string) {
  document.cookie = `${name}=${value}; domain=.cikastudio.com; path=/; max-age=31536000; SameSite=Lax`;
}

const STORAGE_KEY = 'cikastudio-lang';

function detectDefaultLanguage(): Language {
  // Check URL path for explicit language indicator
  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (path.includes('index.zh') || path.includes('index.zh.html')) return 'zh';
    // Also check HTML lang attribute set by server/FOUC script
    const htmlLang = document.documentElement.lang;
    if (htmlLang === 'zh-CN' || htmlLang === 'zh') return 'zh';
  }
  
  const saved = getCookie(STORAGE_KEY);
  if (saved === 'zh' || saved === 'en') return saved;
  
  const browserLangs = navigator.languages || [navigator.language || ''];
  for (const lang of browserLangs) {
    if (lang.startsWith('zh')) return 'zh';
    if (lang.startsWith('en')) return 'en';
  }
  
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const chinaTimezones = [
      'Asia/Shanghai', 'Asia/Chongqing', 'Asia/Urumqi',
      'Asia/Hong_Kong', 'Asia/Macau', 'Asia/Taipei'
    ];
    if (chinaTimezones.includes(timeZone)) return 'zh';
  } catch {
    // Intl not supported
  }
  
  return 'en';
}

type I18nContextType = {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  translations: Record<string, unknown>;
};

const I18nContext = createContext<I18nContextType | null>(null);

export function I18nProvider({ 
  children, 
  toolTranslations,
  translations
}: { 
  children: ReactNode;
  toolTranslations?: { en?: Record<string, unknown>; zh?: Record<string, unknown> };
  translations?: { en?: Record<string, unknown>; zh?: Record<string, unknown> };
}) {
  const [lang, setLangState] = useState<Language>(() => detectDefaultLanguage());

  // Support both prop names for backward compatibility with different main.tsx files
  const mergedToolTranslations = toolTranslations || translations || {};

  // 合并基础翻译 + 工具特有翻译
  const translationsMap = {
    en: { ...baseEn, ...(mergedToolTranslations?.en || {}) },
    zh: { ...baseZh, ...(mergedToolTranslations?.zh || {}) },
  };

  const setLang = useCallback((newLang: Language) => {
    setLangState(newLang);
    setCookie(STORAGE_KEY, newLang);
    document.documentElement.lang = newLang === 'zh' ? 'zh-CN' : 'en';
  }, []);

  useEffect(() => {
    // Cross-tab sync via cookie polling (replaces BroadcastChannel + localStorage for cross-domain)
    const interval = setInterval(() => {
      const cookieLang = getCookie(STORAGE_KEY) as Language;
      // Don't override explicit URL language indicator (e.g., /index.zh)
      const path = window.location.pathname;
      const isExplicitZh = path.includes('index.zh');
      if (isExplicitZh) return; // Respect URL language preference
      if (cookieLang && cookieLang !== lang && (cookieLang === 'zh' || cookieLang === 'en')) {
        setLangState(cookieLang);
        document.documentElement.lang = cookieLang === 'zh' ? 'zh-CN' : 'en';
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [lang]);

  const t = (key: string, params?: Record<string, string | number>): string => {
    const keys = key.split('.');
    let value: unknown = translationsMap[lang];
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        value = translationsMap.en;
        for (const fk of keys) {
          if (value && typeof value === 'object' && fk in value) {
            value = (value as Record<string, unknown>)[fk];
          } else {
            return key;
          }
        }
        break;
      }
    }
    if (typeof value !== 'string') return key;
    
    if (params) {
      return value.replace(/\{(\w+)\}/g, (_, match) => {
        const param = params[match];
        return param !== undefined ? String(param) : `{${match}}`;
      });
    }
    return value;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t, translations: translationsMap[lang] }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}
